/**
 * A escolha para a CCB que já tem caso (E165.F1.T06, GUS-105, ADR-0114 §2). Regra pura, sem JSX.
 *
 * As opções vêm do servidor, por CCB: no caso aberto, vincular (`link`) ou abrir novo (`new`); no
 * fechado, só a nova análise. "Concluir envio" espera uma opção válida para cada uma — o servidor
 * recusa sem ela (422 `ESCOLHA_OBRIGATORIA`). A escolha guardada que deixou de caber (a leitura
 * mudou a situação da CCB) não vale e não vai ao servidor.
 */
import type { ContratoDoEnvioData, EscolhaDoContrato } from '@/lib/abertura_do_caso';

export type EscolhasDoEnvio = Record<string, EscolhaDoContrato>;

export function opcoesDaCcb(contrato: ContratoDoEnvioData): readonly EscolhaDoContrato[] {
  return contrato.opcoes ?? [];
}

/** A escolha válida da CCB, ou `undefined` quando falta ou não cabe mais. */
export function escolhaDaCcb(
  contrato: ContratoDoEnvioData,
  escolhas: EscolhasDoEnvio,
): EscolhaDoContrato | undefined {
  const escolha = escolhas[contrato.chave];
  return escolha !== undefined && opcoesDaCcb(contrato).includes(escolha) ? escolha : undefined;
}

/** As CCBs que pedem escolha e ainda não têm uma válida, na ordem da conferência. */
export function escolhasPendentes(
  contratos: readonly ContratoDoEnvioData[],
  escolhas: EscolhasDoEnvio,
): string[] {
  return contratos
    .filter((c) => opcoesDaCcb(c).length > 0 && escolhaDaCcb(c, escolhas) === undefined)
    .map((c) => c.chave);
}

/** Só as escolhas válidas vão ao servidor. */
export function escolhasParaConcluir(
  contratos: readonly ContratoDoEnvioData[],
  escolhas: EscolhasDoEnvio,
): EscolhasDoEnvio {
  const validas: EscolhasDoEnvio = {};
  for (const contrato of contratos) {
    const escolha = escolhaDaCcb(contrato, escolhas);
    if (escolha !== undefined) validas[contrato.chave] = escolha;
  }
  return validas;
}

/**
 * A CCB pode ir a um caso novo: a nova (sem caso), a que já tinha caso com "abrir novo" e a que
 * ainda espera a escolha e pode abrir um. Só a escolha de vincular (ou a CCB sem escolha, do
 * escritório) garante que nenhum caso novo se abre por ela — desde a E177.F2, a já recebida também
 * pede a escolha.
 */
export function podeIrACasoNovo(contrato: ContratoDoEnvioData, escolhas: EscolhasDoEnvio): boolean {
  if (contrato.situacao === 'novo') return true;
  const escolha = escolhaDaCcb(contrato, escolhas);
  return escolha === undefined ? opcoesDaCcb(contrato).includes('new') : escolha === 'new';
}
